// PricingForm — large reusable form used by both global pricing and
// business-entity pricing pages.  Pass `mode='global'` for global pricing and
// `mode='business-entity'` + `businessEntityId` for BE-specific pricing.
import { useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useDialog } from '@/components/dialogs/useDialog';
import { SimpleConfirmationModal } from '@/components/dialogs/SimpleConfirmationModal';
import { NumberInputField } from '@/components/NumberInputField';
import {
  DistributionTypeEnum,
  InternalFeeOperationTypeEnum,
  OperationActionEnum,
  OperationGroupEnum,
  PeriodTypeEnum,
  isExternalGroup,
} from '@/types/pricing';
import type { GridModel, PricingPayloadModel } from '@/types/pricing';

// ─── Schema ──────────────────────────────────────────────────────────────────

const gridRowSchema = z
  .object({
    /** Backend DB id — stored separately from RHF's auto-generated id */
    gridId: z.union([z.string(), z.number()]).optional(),
    min: z.coerce.number().nonnegative(),
    max: z.coerce.number().nonnegative(),
    fee: z.coerce.number().nonnegative(),
    revenue: z.coerce.number().nonnegative(),
  })
  .refine((g) => g.max >= g.min, {
    message: 'errors.validation.maxGreaterOrEqualMin',
    path: ['max'],
  });

const pricingFormSchema = z
  .object({
    distributionType: z.nativeEnum(DistributionTypeEnum),
    periodType: z.string().nullable().optional(),
    grids: z.array(gridRowSchema),
    fixed: z.coerce.number().nonnegative(),
    percentage: z.coerce.number().nonnegative(),
  })
  .superRefine((data, ctx) => {
    if (
      GRID_DISTRIBUTIONS.includes(data.distributionType) &&
      data.grids.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errors.validation.gridsRequired',
        path: ['grids'],
      });
    }
    if (
      PERIOD_TYPE_DISTRIBUTIONS.includes(data.distributionType) &&
      !data.periodType
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errors.validation.required',
        path: ['periodType'],
      });
    }
    // Ensure grids are ordered: each row's min must be > previous row's max
    for (let i = 1; i < data.grids.length; i++) {
      const prev = data.grids[i - 1];
      const curr = data.grids[i];
      if (curr.min <= prev.max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'errors.validation.gridNotInOrder',
          path: ['grids', i, 'min'],
        });
      }
    }
  });

export type PricingFormValues = z.infer<typeof pricingFormSchema>;

// ─── Distribution-type groupings (mirrors Angular constants) ─────────────────

const GRID_DISTRIBUTIONS: DistributionTypeEnum[] = [
  DistributionTypeEnum.GRID,
  DistributionTypeEnum.PERCENTAGE_GRID,
  DistributionTypeEnum.FIXED_PLUS_BUSINESS_ENTITY_WEIGHT_AVG,
  DistributionTypeEnum.LIMITED_FREE_GRID,
];

const FIXED_DISTRIBUTIONS: DistributionTypeEnum[] = [
  DistributionTypeEnum.FIXED,
  DistributionTypeEnum.FIXED_PLUS_BUSINESS_ENTITY_WEIGHT_AVG,
];

const PERIOD_TYPE_DISTRIBUTIONS: DistributionTypeEnum[] = [
  DistributionTypeEnum.FIXED_PLUS_BUSINESS_ENTITY_WEIGHT_AVG,
  DistributionTypeEnum.LIMITED_FREE_GRID,
];

const EXTERNAL_EXCLUDED_DISTRIBUTIONS: DistributionTypeEnum[] = [
  DistributionTypeEnum.FIXED,
  DistributionTypeEnum.FIXED_PLUS_BUSINESS_ENTITY_WEIGHT_AVG,
  DistributionTypeEnum.LIMITED_FREE_GRID,
];

const ALL_DISTRIBUTION_TYPES = Object.values(DistributionTypeEnum);
const ALL_PERIOD_TYPES = Object.values(PeriodTypeEnum);

// ─── Props ───────────────────────────────────────────────────────────────────

export interface PricingFormProps {
  /**
   * `'global'` — global pricing page.
   * `'business-entity'` — BE-specific pricing page (adds "Use default pricing" button).
   */
  mode: 'global' | 'business-entity';
  businessEntityId?: number;
  /** Pricing data to populate the form.  `undefined` while loading; `null` means no data yet. */
  formData: PricingPayloadModel | null | undefined;
  action: OperationActionEnum;
  operationType: string;
  operationGroup: OperationGroupEnum;
  isRevenueData: boolean;
  /** Whether the current BE pricing is already the default (disables the button). */
  isDefaultPricing?: boolean;
  /** Called with the built payload on successful save confirmation. */
  onSubmit: (data: PricingPayloadModel) => void;
  /** Emitted when the user toggles between VIEW and EDIT modes. */
  onChangeAction: (action: OperationActionEnum) => void;
  /** Emitted when the user confirms "Use default pricing". */
  onSelectDefaultPricing?: () => void;
  isSaving?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function computeAvailableDistributionTypes(
  operationGroup: OperationGroupEnum,
  operationType: string,
  formData?: PricingPayloadModel | null,
): DistributionTypeEnum[] {
  // ADD_WITHDRAW_FUNDS is locked to LIMITED_FREE_GRID
  if (
    operationType.toLowerCase() === InternalFeeOperationTypeEnum.ADD_WITHDRAW_FUNDS
  ) {
    return [DistributionTypeEnum.LIMITED_FREE_GRID];
  }

  let types = ALL_DISTRIBUTION_TYPES;

  if (isExternalGroup(operationGroup)) {
    types = types.filter((t) => !EXTERNAL_EXCLUDED_DISTRIBUTIONS.includes(t));
  }

  if (formData?.enabledDistributionTypes?.length) {
    types = formData.enabledDistributionTypes.filter((t) => types.includes(t));
  }

  if (formData?.disabledDistributionTypes?.length) {
    const disabled = formData.disabledDistributionTypes;
    types = types.filter((t) => !disabled.includes(t));
  }

  return types;
}

function mapFormDataToValues(
  formData: PricingPayloadModel,
): PricingFormValues & { grids: Array<{ gridId?: string | number; min: number; max: number; fee: number; revenue: number }> } {
  const sorted = [...(formData.grids ?? [])].sort((a, b) => a.min - b.min);
  return {
    distributionType: formData.distributionType,
    periodType: formData.periodType ?? null,
    grids: sorted.map((g) => ({
      gridId: g.id,
      min: g.min,
      max: g.max,
      fee: g.fee ?? 0,
      revenue: g.revenue ?? 0,
    })),
    fixed: formData.fixed ?? 0,
    percentage: formData.percentage ?? 0,
  };
}

function mapFormValuesToPayload(
  values: PricingFormValues,
  formData: PricingPayloadModel,
  isRevenueData: boolean,
): PricingPayloadModel {
  const grids: GridModel[] = values.grids.map((row) => {
    const g: GridModel = {
      min: row.min,
      max: row.max,
      ...(row.gridId ? { id: row.gridId } : {}),
      ...(isRevenueData ? { revenue: row.revenue } : { fee: row.fee }),
    };
    return g;
  });

  return {
    id: formData.id,
    distributionType: values.distributionType,
    operationType: formData.operationType,
    grids,
    fixed: values.fixed,
    percentage: values.percentage,
    periodType: values.periodType ?? null,
  };
}

const DEFAULTS: PricingFormValues = {
  distributionType: DistributionTypeEnum.GRID,
  periodType: null,
  grids: [],
  fixed: 0,
  percentage: 0,
};

// ─── Component ───────────────────────────────────────────────────────────────

export function PricingForm({
  mode,
  formData,
  action,
  operationType,
  operationGroup,
  isRevenueData,
  isDefaultPricing,
  onSubmit,
  onChangeAction,
  onSelectDefaultPricing,
  isSaving = false,
}: PricingFormProps) {
  const { t } = useTranslation();
  const { open: openDialog } = useDialog();
  const isViewMode = action === OperationActionEnum.VIEW;
  const hasDefaultPricingButton = mode === 'business-entity';

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<PricingFormValues>({
    resolver: zodResolver(pricingFormSchema),
    defaultValues: DEFAULTS,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'grids',
  });

  const distributionType = watch('distributionType');

  const availableDistributionTypes = useMemo(
    () => computeAvailableDistributionTypes(operationGroup, operationType, formData),
    [operationGroup, operationType, formData],
  );

  // Sync form when formData or action changes
  useEffect(() => {
    if (formData) {
      reset(mapFormDataToValues(formData));
    }
  }, [formData, reset]);

  // Column label for fee/revenue
  const valueColLabel =
    (isRevenueData
      ? t('pages.pricing.revenue', { defaultValue: 'Revenue' })
      : t('pages.pricing.fee', { defaultValue: 'Fee' })) +
    (distributionType === DistributionTypeEnum.PERCENTAGE_GRID ? ' %' : '');

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleAddGrid = () => {
    const lastMax =
      fields.length > 0
        ? (watch(`grids.${fields.length - 1}.max`) as number) + 1
        : 0;
    append({ gridId: undefined, min: lastMax, max: lastMax, fee: 0, revenue: 0 });
  };

  const handleRemoveGrid = async (index: number) => {
    const confirmed = await openDialog<boolean | null>((close) => (
      <SimpleConfirmationModal
        titleTranslatePath="modal.simple_confirmation.remove_grid.title"
        subtitleTranslatePath="modal.simple_confirmation.remove_grid.subtitle"
        params={{ position: index + 1 }}
        onClose={close}
      />
    ));
    if (confirmed) remove(index);
  };

  const handleSave = handleSubmit(async (values) => {
    if (!formData) return;
    const confirmed = await openDialog<boolean | null>((close) => (
      <SimpleConfirmationModal
        titleTranslatePath="modal.simple_confirmation.save_changes.title"
        subtitleTranslatePath="modal.simple_confirmation.save_changes.subtitle"
        onClose={close}
      />
    ));
    if (confirmed) {
      onSubmit(mapFormValuesToPayload(values, formData, isRevenueData));
    }
  });

  const handleCancel = () => {
    if (formData) reset(mapFormDataToValues(formData));
    onChangeAction(OperationActionEnum.VIEW);
  };

  const handleUseDefaultPricing = async () => {
    const confirmed = await openDialog<boolean | null>((close) => (
      <SimpleConfirmationModal
        titleTranslatePath="modal.simple_confirmation.set_default_pricing.title"
        subtitleTranslatePath="modal.simple_confirmation.set_default_pricing.subtitle"
        selectedIconPath="icons/actions/alert-triangle.svg"
        iconStyles={{ width: '3em' }}
        onClose={close}
      />
    ));
    if (confirmed) onSelectDefaultPricing?.();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const showPercentage = distributionType === DistributionTypeEnum.PERCENTAGE;
  const showFixed = FIXED_DISTRIBUTIONS.includes(distributionType);
  const showGrids = GRID_DISTRIBUTIONS.includes(distributionType);
  const showPeriodType = PERIOD_TYPE_DISTRIBUTIONS.includes(distributionType);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      {/* Distribution type + Period type selectors */}
      <Stack direction="row" spacing={4} sx={{ mb: 3 }}>
        {/* Distribution type */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('pages.pricing.distribution_type', { defaultValue: 'Distribution type' })}
          </Typography>
          <Controller
            name="distributionType"
            control={control}
            render={({ field }) => (
              <FormControl size="small" error={!!errors.distributionType}>
                <Select
                  {...field}
                  disabled={isViewMode}
                  sx={{ minWidth: 260 }}
                >
                  {availableDistributionTypes.map((dt) => (
                    <MenuItem key={dt} value={dt}>
                      {t(`pages.pricing.distribution_types.${dt}`, {
                        defaultValue: dt,
                      })}
                    </MenuItem>
                  ))}
                </Select>
                {errors.distributionType && (
                  <FormHelperText>
                    {t(errors.distributionType.message ?? '', {
                      defaultValue: errors.distributionType.message,
                    })}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>

        {/* Period type (conditional) */}
        {showPeriodType && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {t('pages.pricing.period_type', { defaultValue: 'Period type' })}
            </Typography>
            <Controller
              name="periodType"
              control={control}
              render={({ field }) => (
                <FormControl size="small" error={!!errors.periodType}>
                  <Select
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? null : e.target.value)
                    }
                    disabled={isViewMode}
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value="">
                      {t('pages.pricing.period_types.null', {
                        defaultValue: 'None',
                      })}
                    </MenuItem>
                    {ALL_PERIOD_TYPES.map((pt) => (
                      <MenuItem key={pt} value={pt}>
                        {t(`pages.pricing.period_types.${pt}`, {
                          defaultValue: pt,
                        })}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.periodType && (
                    <FormHelperText>
                      {t(errors.periodType.message ?? '', {
                        defaultValue: errors.periodType.message,
                      })}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Box>
        )}
      </Stack>

      {/* Percentage / Fixed inputs */}
      {(showPercentage || showFixed) && (
        <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
          {showPercentage && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('pages.pricing.fixed_percentage', { defaultValue: 'Percentage (%)' })}
              </Typography>
              <Controller
                name="percentage"
                control={control}
                render={({ field, fieldState }) => (
                  <NumberInputField
                    size="small"
                    disabled={isViewMode}
                    value={field.value}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    onBlur={field.onBlur}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{ endAdornment: <Typography variant="body2">%</Typography> }}
                    sx={{ width: 160 }}
                  />
                )}
              />
            </Box>
          )}
          {showFixed && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {operationGroup.includes('fee')
                  ? t('pages.pricing.fixed_flat_fee', { defaultValue: 'Fixed flat fee' })
                  : t('pages.pricing.fixed_flat_revenue', { defaultValue: 'Fixed flat revenue' })}
              </Typography>
              <Controller
                name="fixed"
                control={control}
                render={({ field, fieldState }) => (
                  <NumberInputField
                    size="small"
                    disabled={isViewMode}
                    value={field.value}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    onBlur={field.onBlur}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>XAF</Typography> }}
                    sx={{ width: 200 }}
                  />
                )}
              />
            </Box>
          )}
        </Stack>
      )}

      {/* Grids table */}
      {showGrids && (
        <Box sx={{ flexGrow: 1 }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: 40 }}>#</TableCell>
                  <TableCell>
                    {t('pages.pricing.from', { defaultValue: 'From' })}
                  </TableCell>
                  <TableCell>
                    {t('pages.pricing.to', { defaultValue: 'To' })}
                  </TableCell>
                  <TableCell>{valueColLabel}</TableCell>
                  {!isViewMode && <TableCell sx={{ width: 48 }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow key={field.id} hover>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell>
                      <Controller
                        name={`grids.${index}.min`}
                        control={control}
                        render={({ field: f, fieldState }) => (
                          <NumberInputField
                            size="small"
                            disabled={isViewMode}
                            value={f.value}
                            onChange={(e) => f.onChange(Number(e.target.value))}
                            onBlur={f.onBlur}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message
                              ? t(fieldState.error.message, { defaultValue: fieldState.error.message })
                              : undefined}
                            sx={{ width: 120 }}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        name={`grids.${index}.max`}
                        control={control}
                        render={({ field: f, fieldState }) => (
                          <NumberInputField
                            size="small"
                            disabled={isViewMode}
                            value={f.value}
                            onChange={(e) => f.onChange(Number(e.target.value))}
                            onBlur={f.onBlur}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message
                              ? t(fieldState.error.message, { defaultValue: fieldState.error.message })
                              : undefined}
                            sx={{ width: 120 }}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        name={
                          isRevenueData
                            ? `grids.${index}.revenue`
                            : `grids.${index}.fee`
                        }
                        control={control}
                        render={({ field: f, fieldState }) => (
                          <NumberInputField
                            size="small"
                            disabled={isViewMode}
                            value={f.value}
                            onChange={(e) => f.onChange(Number(e.target.value))}
                            onBlur={f.onBlur}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message
                              ? t(fieldState.error.message, { defaultValue: fieldState.error.message })
                              : undefined}
                            InputProps={
                              distributionType === DistributionTypeEnum.PERCENTAGE_GRID
                                ? { endAdornment: <Typography variant="body2">%</Typography> }
                                : undefined
                            }
                            sx={{ width: 140 }}
                          />
                        )}
                      />
                    </TableCell>
                    {!isViewMode && (
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => void handleRemoveGrid(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {fields.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isViewMode ? 4 : 5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {t('pages.pricing.emptyTable', { defaultValue: 'No grid rows yet' })}
                      </Typography>
                      {errors.grids?.root?.message && (
                        <Typography variant="caption" color="error">
                          {t(errors.grids.root.message, {
                            defaultValue: errors.grids.root.message,
                          })}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!isViewMode && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <IconButton color="primary" onClick={handleAddGrid} size="small">
                  <AddCircleOutlineIcon />
                </IconButton>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Action buttons */}
      <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
        {isViewMode ? (
          <Button
            variant="contained"
            color="error"
            startIcon={<EditIcon />}
            onClick={() => onChangeAction(OperationActionEnum.EDIT)}
          >
            {t('global.edit', { defaultValue: 'Edit' })}
          </Button>
        ) : (
          <>
            {hasDefaultPricingButton && (
              <Button
                variant="outlined"
                color={isDefaultPricing ? 'inherit' : 'warning'}
                disabled={isDefaultPricing}
                startIcon={<WarningAmberIcon />}
                onClick={() => void handleUseDefaultPricing()}
              >
                {t('global.buttons.use_default_pricing', {
                  defaultValue: 'Use default pricing',
                })}
              </Button>
            )}
            <Button
              variant="contained"
              color="error"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={isSaving}
              onClick={() => void handleSave()}
            >
              {t('global.buttons.save_changes', { defaultValue: 'Save changes' })}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
            >
              {t('global.buttons.cancel', { defaultValue: 'Cancel' })}
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
}
